import { type RenderFieldExtensionCtx } from 'datocms-plugin-sdk';
import { Button, SwitchInput } from 'datocms-react-ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AITextControlSelector } from './AITextControlSelector';
import { ColorControlSelector } from './ColorControlSelector';
import { EditableLabel } from './EditableLabel';
import { ImageLogoControlSelector } from './ImageLogoControlSelector';
import { PresetTranslationsEditor } from './PresetTranslationsEditor';
import { SpeakerControlSelector } from './SpeakerControlsSelector';
import { TextControlSelector } from './TextControlSelector';

import type { AITextConfigPatch } from './AITextControlSelector';
import type { GraphicsPromptOption } from './CompositionControlsGenerateService';
import type {
  ControlConfiguration,
  DefaultBrandKitValue,
  SpeakerBindingKey,
} from '@src/components/sidepanel/CompositionSettings/types';

type ControlRowProps = {
  ctx: RenderFieldExtensionCtx;
  control: ControlConfiguration & { enabled?: boolean };
  onToggleEnabled: () => void;
  onUpdateLabel: (newLabel: string) => void;
  onUpdateCustomValue: (
    newValue:
      | {
          value?: string | number;
          brandKit?: DefaultBrandKitValue;
        }
      | undefined,
  ) => void;
  setValueError: (error: string) => void;
  onUpdateSpeakerControl: (id: string, fieldType: SpeakerBindingKey) => void;
  onUpdateControlType: (
    value: 'image' | 'logo' | 'headshot' | 'text' | 'aiText',
  ) => void;
  onUpdateControlId: (id: string) => void;
  onUpdateAITextConfig: (patch: AITextConfigPatch) => void;
  graphicsPrompts: GraphicsPromptOption[] | null;
  promptsLoadFailed: boolean;
  onSelectMergeField: (property: string, checked: boolean) => void;
  selectedMergeFieldProperty: string | null;
  speakerTextFieldsOptions: { id: string; label: string }[];
  speakerHeadshotOptions: { id: string; label: string }[];
  onSplitField: () => void;
  imageOptions: { id: string; label: string; url: string }[];
  logoOptions: { id: string; label: string; url: string }[];
  mediaRefetch: () => void;
  onToggleAllowTranslation: (checked: boolean) => void;
  onToggleAllowQuickEdit: (checked: boolean) => void;
  onUpdatePresetTranslation: (languageCode: string, value: string) => void;
  onRemovePresetTranslation: (languageCode: string) => void;
};

const rgbaRegex =
  /rgba?\((25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d),\s?(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d),\s?(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\s?,\s?([0-9.]+%?))?\)/gi;

const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export function ControlRow({
  ctx,
  control,
  onToggleEnabled,
  onUpdateLabel,
  onUpdateCustomValue,
  onUpdateSpeakerControl,
  setValueError,
  onUpdateControlType,
  onUpdateControlId,
  onUpdateAITextConfig,
  graphicsPrompts,
  promptsLoadFailed,
  onSelectMergeField,
  selectedMergeFieldProperty,
  speakerTextFieldsOptions,
  speakerHeadshotOptions,
  imageOptions,
  logoOptions,
  onSplitField,
  mediaRefetch,
  onToggleAllowTranslation,
  onToggleAllowQuickEdit,
  onUpdatePresetTranslation,
  onRemovePresetTranslation,
}: ControlRowProps) {
  const isCustomValue =
    control.default?.brandKit === undefined &&
    control.default?.quote === undefined &&
    control.default?.value !== undefined;

  const [customValue, setCustomValue] = useState<string | undefined>(
    control.default?.value as string | undefined,
  );
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const validateAndUpdate = useCallback(
    (
      newValue:
        | {
            value?: string | number;
          }
        | undefined,
    ) => {
      if (newValue?.value) {
        const matchesRgba = rgbaRegex.test(newValue.value.toString());
        const matchesHex = hexRegex.test(newValue.value.toString());

        if (!matchesRgba && !matchesHex) {
          const errorMsg =
            'Invalid value. Please enter a valid RGBA or hex value.';
          ctx.alert(errorMsg);
          setValueError(errorMsg);
          return;
        }
        ctx.notice('Value is valid');
        onUpdateCustomValue(newValue);
      }
    },
    [ctx, onUpdateCustomValue, setValueError],
  );

  const handleUpdateCustomValue = (
    newValue:
      | {
          value?: string | number;
        }
      | undefined,
  ) => {
    // Update the local state immediately for responsive UI
    setCustomValue(newValue?.value?.toString());

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set up new debounced validation (500ms delay)
    debounceTimerRef.current = setTimeout(() => {
      validateAndUpdate(newValue);
    }, 1000);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyItems: 'center',
        padding: '8px',
        margin: '8px 0px',
        backgroundColor: 'var(--disabled-bg-color)',
        borderRadius: '4px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <SwitchInput
            id={control.label}
            name={control.label}
            value={control.enabled ?? true}
            onChange={onToggleEnabled}
          />
          <EditableLabel label={control.label} onUpdate={onUpdateLabel} />
          {control.type === 'color' && (
            <ColorControlSelector
              control={control}
              onUpdateCustomValue={onUpdateCustomValue}
              isCustomValue={isCustomValue}
              customValue={customValue}
              setCustomValue={setCustomValue}
              handleUpdateCustomValue={handleUpdateCustomValue}
            />
          )}
          {(control.type === 'image' || control.type === 'logo') && (
            <ImageLogoControlSelector
              ctx={ctx}
              control={control}
              onUpdateControlType={onUpdateControlType}
              onUpdateCustomValue={onUpdateCustomValue}
              imageOptions={imageOptions}
              logoOptions={logoOptions}
              mediaRefetch={mediaRefetch}
            />
          )}
          {control.type === 'headshot' && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'var(--accent-color)',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: 'var(--light-bg-color)',
              }}
            >
              Headshot
            </span>
          )}
          {(control.type === 'text' || control.type === 'number') && (
            <TextControlSelector
              control={control}
              onUpdateCustomValue={onUpdateCustomValue}
              isCustomValue={isCustomValue}
              customValue={customValue}
              setCustomValue={setCustomValue}
            />
          )}
          {(control.type === 'text' || control.type === 'aiText') && (
            <AITextControlSelector
              control={control}
              onUpdateControlType={onUpdateControlType}
              onUpdateControlId={onUpdateControlId}
              onUpdateAITextConfig={onUpdateAITextConfig}
              graphicsPrompts={graphicsPrompts}
              promptsLoadFailed={promptsLoadFailed}
            />
          )}
          {control.type === 'speaker' && (
            <SpeakerControlSelector
              control={control}
              onUpdateSpeaker={onUpdateSpeakerControl}
              options={speakerTextFieldsOptions}
              headshotOptions={speakerHeadshotOptions}
            />
          )}
        </div>
        <span style={{ fontSize: '12px', color: 'var(--disabled-text-color)' }}>
          Property: {control.property} | Type: {control.type} | Paths:{' '}
          {control.elementPaths.length}
          {control.type === 'aiText' &&
            ` | Prompt: ${control.promptTitle || 'not selected'}`}
        </span>
        {(control.type === 'text' || control.type === 'aiText') && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <label
              style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
                fontSize: '12px',
              }}
            >
              <input
                type="checkbox"
                checked={!!control.allowTranslation}
                onChange={e => onToggleAllowTranslation(e.target.checked)}
              />
              Allow translation
            </label>
            <label
              style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
                fontSize: '12px',
              }}
            >
              <input
                type="checkbox"
                checked={!!control.allowQuickEdit}
                onChange={e => onToggleAllowQuickEdit(e.target.checked)}
              />
              Allow quick edit
            </label>
          </div>
        )}
        {(control.type === 'text' || control.type === 'aiText') &&
          control.allowTranslation && (
            <PresetTranslationsEditor
              translations={control.translations}
              onUpdate={onUpdatePresetTranslation}
              onRemove={onRemovePresetTranslation}
            />
          )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
          marginLeft: 'auto',
          marginRight: '8px',
        }}
      >
        {control.elementPaths.length > 1 && (
          <Button
            buttonSize="xxs"
            type="button"
            style={{ marginRight: '8px', padding: '4px 8px' }}
            onClick={() => onSplitField()}
          >
            <span
              style={{ fontSize: '12px', color: 'var(--disabled-text-color)' }}
            >
              Split
            </span>
          </Button>
        )}
        {control.type !== 'speaker' &&
          control.type !== 'video' &&
          control.type !== 'image' &&
          control.type !== 'logo' &&
          control.type !== 'headshot' && (
            <input
              type="checkbox"
              disabled={
                !!selectedMergeFieldProperty &&
                selectedMergeFieldProperty !== control.property
              }
              onChange={e =>
                onSelectMergeField(control.property, e.target.checked)
              }
            />
          )}
      </div>
    </div>
  );
}
